import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Trash2, Plus, Upload } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface LineItem {
  quantity: number;
  item_number: string;
  description: string;
  unit_price: number;
  total_price: number;
}

interface POUploadFormProps {
  onSuccess: () => void;
}

export function POUploadForm({ onSuccess }: POUploadFormProps) {
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [poFile, setPOFile] = useState<File | null>(null);
  
  const [poData, setPOData] = useState({
    po_number: '',
    po_date: '',
    ship_date: '',
    tax_exemption_number: '',
    
    // Bill to info
    bill_to_name: '',
    bill_to_address: '',
    bill_to_city: '',
    bill_to_state: '',
    bill_to_zip: '',
    
    // Ship to info
    ship_to_name: '',
    ship_to_address: '',
    ship_to_city: '',
    ship_to_state: '',
    ship_to_zip: '',
    
    // Financial
    subtotal: 0,
    shipping_cost: 0,
    tax_amount: 0,
    total_amount: 0,
    
    // Additional
    comments: '',
    terms: 'Net 30 days',
    account_code: '',
    requested_by: '',
    department: '',
    approved_by: '',
  });

  const [lineItems, setLineItems] = useState<LineItem[]>([
    { quantity: 1, item_number: 'EDUGOGY-SUBSCRIPTION', description: 'Edugogy Professional Development Platform - Annual License', unit_price: 0, total_price: 0 }
  ]);

  const handleInputChange = (field: string, value: string | number) => {
    setPOData(prev => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (index: number, field: keyof LineItem, value: string | number) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Recalculate total for this item
      if (field === 'quantity' || field === 'unit_price') {
        updated[index].total_price = updated[index].quantity * updated[index].unit_price;
      }
      
      return updated;
    });
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      quantity: 1,
      item_number: '',
      description: '',
      unit_price: 0,
      total_price: 0
    }]);
  };

  const removeLineItem = (index: number) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.total_price, 0);
    const total = subtotal + poData.shipping_cost + poData.tax_amount;
    
    setPOData(prev => ({
      ...prev,
      subtotal,
      total_amount: total
    }));
  };

  // Recalculate totals when line items or shipping/tax change
  useEffect(() => {
    calculateTotals();
  }, [lineItems, poData.shipping_cost, poData.tax_amount]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPOFile(file);
    }
  };

  const uploadFile = async () => {
    if (!poFile || !user) return null;

    const fileExt = poFile.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('purchase-orders')
      .upload(fileName, poFile);

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('purchase-orders')
      .getPublicUrl(fileName);

    return { url: publicUrl, name: poFile.name };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !userProfile?.district_id) return;

    setLoading(true);
    
    try {
      // Upload file if provided
      let fileInfo = null;
      if (poFile) {
        fileInfo = await uploadFile();
      }

      // Create PO record
      const { data: poRecord, error: poError } = await supabase
        .from('purchase_orders')
        .insert({
          ...poData,
          district_id: userProfile.district_id,
          uploaded_by: user.id,
          po_file_url: fileInfo?.url,
          po_file_name: fileInfo?.name,
        })
        .select()
        .single();

      if (poError) throw poError;

      // Create line items
      const lineItemsToInsert = lineItems.map(item => ({
        po_id: poRecord.id,
        ...item
      }));

      const { error: lineItemsError } = await supabase
        .from('po_line_items')
        .insert(lineItemsToInsert);

      if (lineItemsError) throw lineItemsError;

      onSuccess();
    } catch (error) {
      console.error('Error uploading PO:', error);
      toast({
        title: "Error",
        description: "Failed to upload purchase order",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* File Upload Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            <CardTitle>Upload Purchase Order File</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="po_file">Upload PO File</Label>
            <Input
              id="po_file"
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={handleFileChange}
            />
          </div>

          {poFile && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Upload className="h-4 w-4" />
              <span className="text-sm font-medium">{poFile.name}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Basic PO Information */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Order Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="po_number">PO Number *</Label>
              <Input
                id="po_number"
                value={poData.po_number}
                onChange={(e) => handleInputChange('po_number', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="po_date">PO Date *</Label>
              <Input
                id="po_date"
                type="date"
                value={poData.po_date}
                onChange={(e) => handleInputChange('po_date', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="ship_date">Ship Date</Label>
              <Input
                id="ship_date"
                type="date"
                value={poData.ship_date}
                onChange={(e) => handleInputChange('ship_date', e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="tax_exemption_number">Tax Exemption Number</Label>
            <Input
              id="tax_exemption_number"
              value={poData.tax_exemption_number}
              onChange={(e) => handleInputChange('tax_exemption_number', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Billing Information */}
      <Card>
        <CardHeader>
          <CardTitle>Billing Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="bill_to_name">Bill To Name *</Label>
            <Input
              id="bill_to_name"
              value={poData.bill_to_name}
              onChange={(e) => handleInputChange('bill_to_name', e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="bill_to_address">Address *</Label>
            <Input
              id="bill_to_address"
              value={poData.bill_to_address}
              onChange={(e) => handleInputChange('bill_to_address', e.target.value)}
              required
            />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="bill_to_city">City *</Label>
              <Input
                id="bill_to_city"
                value={poData.bill_to_city}
                onChange={(e) => handleInputChange('bill_to_city', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="bill_to_state">State *</Label>
              <Input
                id="bill_to_state"
                value={poData.bill_to_state}
                onChange={(e) => handleInputChange('bill_to_state', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="bill_to_zip">ZIP *</Label>
              <Input
                id="bill_to_zip"
                value={poData.bill_to_zip}
                onChange={(e) => handleInputChange('bill_to_zip', e.target.value)}
                required
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Order Details</CardTitle>
            <Button type="button" onClick={addLineItem} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {lineItems.map((item, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Item {index + 1}</h4>
                {lineItems.length > 1 && (
                  <Button
                    type="button"
                    onClick={() => removeLineItem(index)}
                    variant="outline"
                    size="sm"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              <div className="grid md:grid-cols-5 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => handleLineItemChange(index, 'quantity', Number(e.target.value))}
                  />
                </div>
                <div>
                  <Label>Item Number</Label>
                  <Input
                    value={item.item_number}
                    onChange={(e) => handleLineItemChange(index, 'item_number', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Unit Price</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.unit_price}
                    onChange={(e) => handleLineItemChange(index, 'unit_price', Number(e.target.value))}
                  />
                </div>
                <div>
                  <Label>Total</Label>
                  <Input
                    value={`$${item.total_price.toFixed(2)}`}
                    disabled
                  />
                </div>
              </div>
            </div>
          ))}
          
          <Separator />
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="shipping_cost">Shipping Cost</Label>
              <Input
                id="shipping_cost"
                type="number"
                step="0.01"
                min="0"
                value={poData.shipping_cost}
                onChange={(e) => handleInputChange('shipping_cost', Number(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="tax_amount">Tax Amount</Label>
              <Input
                id="tax_amount"
                type="number"
                step="0.01"
                min="0"
                value={poData.tax_amount}
                onChange={(e) => handleInputChange('tax_amount', Number(e.target.value))}
              />
            </div>
            <div>
              <Label>Total Amount</Label>
              <Input
                value={`$${poData.total_amount.toFixed(2)}`}
                disabled
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="requested_by">Requested By</Label>
              <Input
                id="requested_by"
                value={poData.requested_by}
                onChange={(e) => handleInputChange('requested_by', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="department">Department</Label>
              <Input
                id="department"
                value={poData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="account_code">Account Code</Label>
              <Input
                id="account_code"
                value={poData.account_code}
                onChange={(e) => handleInputChange('account_code', e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="approved_by">Approved By</Label>
            <Input
              id="approved_by"
              value={poData.approved_by}
              onChange={(e) => handleInputChange('approved_by', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="comments">Comments</Label>
            <Textarea
              id="comments"
              value={poData.comments}
              onChange={(e) => handleInputChange('comments', e.target.value)}
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="po_file">Upload PO File (PDF, DOC, etc.)</Label>
            <Input
              id="po_file"
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={handleFileChange}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button type="submit" disabled={loading}>
          {loading ? 'Uploading...' : 'Upload Purchase Order'}
        </Button>
      </div>
    </form>
  );
}